import firebase from "firebase/compat/app";
import { createContext, useEffect, useState } from "react";
import { useAuthState } from 'react-firebase-hooks/auth';
import { User } from '../../shared/types';
import { analyticsService } from "../analytics";
import { captureFirebaseErrorForSentry } from "../sentryCapture";
import { firestore } from "./app";
import { addNewUserInfo, createUserUpdate, updateUserLastSignInTime } from "./writes";

export const UserContext = createContext<User | undefined>(undefined);
UserContext.displayName = "UserContext"; // https://reactjs.org/docs/context.html#contextdisplayname

export function useFirebaseAuth() {
    const [userAuthed, userLoading, userError] = useAuthState(firebase.auth());
    const [user, setUser] = useState<User>();

    useEffect(() => {
        if (!userLoading && userAuthed?.uid) {
            const unsubscribe = firestore.collection('users').doc(firebase.auth().currentUser?.uid).onSnapshot((doc) => {

                const userDoc = doc.data() as User;

                setUser({
                    ...userDoc,
                    id: firebase.auth().currentUser?.uid,
                });
            });

            // https://blog.logrocket.com/understanding-react-useeffect-cleanup-function/
            // https://benmcmahen.com/using-firebase-with-react-hooks/
            return () => unsubscribe()
        }
    }, [userLoading, userAuthed]);

    const signIn = async (email: string, password: string) => {
        try {
            const res = await firebase.auth().signInWithEmailAndPassword(email, password);

            if (res.user?.uid) {
                console.log("DOING IT!");
                await updateUserLastSignInTime(res.user?.uid);
            }

            analyticsService().logEvent("Logged In");
            analyticsService().setUserId(res.user!.uid);
            analyticsService().setUserProperty('email', email);

            return res;
        } catch (e) {
            analyticsService().logError(e);
            return e;
        }
    };

    const signUp = async (email: string, password: string) => {
        try {
            const res = await firebase.auth().createUserWithEmailAndPassword(email, password);

            const orgDomain = email.split('@')[1]?.toLowerCase() || null;

            if (res.additionalUserInfo?.isNewUser) {
                await addNewUserInfo(res.user!.uid, createUserUpdate(orgDomain === 'grail-talent.com'
                    ? { email, orgDomain, roles: {} }
                    : { email, orgDomain, roles: { booker: true }, bookerSelfRegistered: true }));
            }

            analyticsService().logEvent("Signed Up");
            analyticsService().setUserId(res.user!.uid);
            analyticsService().setUserProperty('email', email);

            return res;
        } catch (e) {
            analyticsService().logError(e);
            captureFirebaseErrorForSentry(e, 'signUp');
            return e;
        }
    }

    const resetPassword = async (email: string) => {
        try {
            const res = await firebase.auth().sendPasswordResetEmail(email);
            analyticsService().logEvent("Requested New Password", { email });
            return res;
        } catch (e) {
            analyticsService().logError(e);
            return e;
        }
    }

    const signOut = () => {
        firebase.auth().signOut();
        analyticsService().logEvent("Signed Out");
        analyticsService().logOut();

        setTimeout(function () {
            // reload the page after logout to properly handle drift...
            window.location.href = '/';
        }, 500);
    };

    return { user, userAuthed, userLoading, userError, signIn, signUp, signOut, resetPassword }; // isAdmin
}
